import { IconMenuDeep, IconPhonePlus, IconSearch, IconX } from '@tabler/icons-react';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, To } from 'react-router-dom';

export default function SubNavbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('');
  const [navbarBg, setNavbarBg] = useState('');
  const [isLogoBig, setIsLogoBig] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const isHome = location.pathname === '/home';

      if (isHome && scrollY < 50) {
        setNavbarBg('bg-gradient-to-b from-[black] via-[#000000ab]     to-transparent  h-36 pb-3 -mt-8');
        setIsLogoBig(true);
      } else {
        setNavbarBg('bg-gradient-to-r from-[#2f2c26] via-[#453f33]  to-[#673f26]  backdrop-blur-md shadow-lg');
        setIsLogoBig(false);
      }

      const aboutSection = document.getElementById('about');
      if (aboutSection && isHome) {
        const { top, bottom } = aboutSection.getBoundingClientRect();
        setActiveLink(top <= window.innerHeight / 2 && bottom >= window.innerHeight / 2 ? 'about' : 'home');
      } else {
        setActiveLink(location.pathname.replace('/', '') || 'home');
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location]);

  const handleScrollToSection = (sectionId: string) => {
    if (location.pathname !== '/home') {
      navigate('/home');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavigate = (path: To, scrollToTop = false) => {
    navigate(path);
    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const links = [
    { to: '/home', label: 'Home', scrollTo: null },
    { to: '/home', label: 'About Us', scrollTo: 'about' },
    { to: '/allproducts', label: 'Products' },
    { to: '/Software-solution', label: 'Software Solutions' },
    { to: '/events', label: 'Events & Gallery' },
    { to: '/contact', label: 'Contact Us' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full h-16 z-[99] font-gotham px-6 md:px-12 flex justify-between items-center transition-all duration-500 ease-in-out  ${navbarBg}`}
    >
      {/* Logo Section */}
      <div
        className="flex items-center gap-3 cursor-pointer group"
        onClick={() => handleNavigate('/home', true)}
      >
        <img
          src="/assets/trioslogo.png"
          alt="Trios International Logo"
          className={`transition-all duration-500 ease-in-out transform group-hover:scale-110 ${
            isLogoBig ? 'md:h-10 h-10' : 'md:h-10 h-8'
          }`}
        />
        <h1
          className={`flex flex-col gap-0 leading-none font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500 transition-all duration-500 ${
            isLogoBig ? 'md:text-[1.2rem] text-xl' : 'md:text-[1.2rem] text-lg'
          }`}
        >
          <span>Trios</span>
          <span>International</span>
        </h1>
      </div>

      {/* Desktop Menu */}
      <div className="hidden md:flex items-center gap-2 text-lg font-medium text-gray-100">
        {links.map((item) => (
          <button
            key={item.to}
            onClick={() =>
              item.scrollTo
                ? handleScrollToSection(item.scrollTo)
                : handleNavigate(item.to, item.to === '/home')
            }
            className={`relative px-3 py-1 transition-all duration-300 ease-in-out text-nowrap text-sm ${
              activeLink === (item.scrollTo || item.to.replace('/', ''))
                ? 'text-red-400 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-red-400'
                : 'hover:text-red-300 hover:after:absolute hover:after:bottom-0 hover:after:left-0 hover:after:w-full hover:after:h-0.5 hover:after:bg-red-300'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Mobile Menu Button */}
      <button
        className="md:hidden text-white hover:text-red-400 transition-colors duration-300"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        <IconMenuDeep stroke={2} size={28} />
      </button>

      {/* Mobile Menu */}
      <div
        className={`fixed top-0 right-0 h-screen w-3/4 max-w-xs bg-gradient-to-b from-gray-900 to-black text-white flex flex-col items-center justify-center gap-8 md:hidden transform transition-transform duration-500 ease-in-out shadow-2xl ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <button
          className="absolute top-6 right-6 text-red-400 hover:text-red-500 transition-colors duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <IconX stroke={2} size={32} />
        </button>
        {links.map((item) => (
          <button
            key={item.to}
            onClick={() => {
              if (item.scrollTo) {
                handleScrollToSection(item.scrollTo);
              } else {
                handleNavigate(item.to, item.to === '/home');
              }
              setIsMobileMenuOpen(false);
            }}
            className={`text-lg font-medium transition-all duration-300 ${
              activeLink === (item.scrollTo || item.to.replace('/', ''))
                ? 'text-red-400 scale-105'
                : 'text-gray-100 hover:text-red-400 hover:scale-105'
            }`}
          >
            {item.label}
          </button>
        ))}
        <div className="flex gap-6 mt-8">
          <button className="text-gray-100 hover:text-red-400 transition-colors duration-300">
            <IconPhonePlus stroke={2} size={28} />
          </button>
          <button className="text-gray-100 hover:text-red-400 transition-colors duration-300">
            <IconSearch stroke={2} size={28} />
          </button>
        </div>
      </div>
    </nav>
  );
}